import React from "react";
import { Steps, Tooltip, Button } from "antd";
import type { StepsProps } from "antd";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import { JobApplication } from "../../../../Entities/JobApplication";
import { MatchResult } from "../../../../Entities/MatchResult";
import { Session } from "../../../../Entities/Session";
import dayjs from "dayjs";
import { 
  isProfileMatchCompleted, 
  isQuestionnaireCompleted, 
  isQuestionnairePending as isQuestionnairePendingHelper
} from "../../utils/job.application.helper.functions";
import { CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";

interface ApplicationStageStepsProps {
  jobPost: JobDescriptionEntity;
  jobApplication: JobApplication;
  interviewSessions: Session[];
  currentStatusMessage?: string;
  setActiveTabKey: (key: string) => void;
  profileMatchResult: MatchResult | null;
}

const ApplicationStageSteps: React.FC<ApplicationStageStepsProps> = ({
  jobPost,
  jobApplication,
  interviewSessions,
  currentStatusMessage,
  setActiveTabKey,
  profileMatchResult,
}) => {
  // Use single source of truth helper function
  const isResumeUploaded = isProfileMatchCompleted(jobApplication);

  // Use helper function and combine with component-specific conditions
  // If questionnaire is completed (qualified/unqualified), it's done regardless of sessions
  // Sessions are required for pending state, but completed status means it's done
  const isQuestionnaireDone = Boolean(
    isQuestionnaireCompleted(jobApplication) && 
    (interviewSessions.length > 0 || 
     jobApplication?.questionnaireStatus === "qualified" || 
     jobApplication?.questionnaireStatus === "unqualified")
  );

  // Use helper function and combine with component-specific conditions
  const isQuestionnairePending = Boolean(
    isQuestionnairePendingHelper(jobApplication) &&
    interviewSessions.length > 0 &&
    jobApplication?.status === "applied"
  );

  const isSlotSelectionDone = Boolean(jobApplication?.interviewSchedule);
  const isSlotSelectionPending = Boolean(
    jobApplication?.status === "interview" && !isSlotSelectionDone
  );
  const isShortlisted = Boolean(
    jobApplication?.status === "shortlisted" ||
      jobApplication?.questionnaireStatus === "qualified"
  );

  // Determine if Assessments stage should be shown (based on job post configuration)
  const hasInterviewRounds = Boolean(
    jobPost?.interviewRounds && jobPost.interviewRounds.length > 0
  );


  const getStepIcon = (semanticState: "success" | "error" | "neutral") => {
    switch (semanticState) {
      case "success":
        return <CheckCircleOutlined style={{ fontSize: 16 }} />;
      case "error":
        return <CloseCircleOutlined style={{ fontSize: 16 }} />;
      case "neutral":
      default:
        return <QuestionCircleOutlined style={{ fontSize: 16 }} />;
    }
  };

  
  // Helper function to determine step semantic state (success, error, neutral)
  const getStepSemanticState = (stepTitle: string): "success" | "error" | "neutral" => {
    switch (stepTitle) {
      case "Applied":
      case "Application": {
        // Application step is always success (already applied)
        return "success";
      }
      
      case "Profile Match": {
        // First check if resume was uploaded (profileId exists)
        const hasResumeUploaded = Boolean(jobApplication?.profileId);
        
        if (!hasResumeUploaded) {
          return "neutral"; // Pending
        }
        
        // Use profileMatchResult status as primary source of truth (same as Profile Match tab)
        // Fall back to jobApplication.profileMatchStatus.status if profileMatchResult is not available
        const statusFromResult = profileMatchResult?.status;
        const profileMatchStatus = statusFromResult || jobApplication?.profileMatchStatus?.status;
        
        const isManuallyMovedToAI = 
          profileMatchStatus === "unqualified" && 
          interviewSessions.length > 0 &&
          hasInterviewRounds &&
          (jobApplication?.status === "applied" || jobApplication?.status === "shortlisted" || jobApplication?.status === "interview");
        
        if (isManuallyMovedToAI) {
          return "error"; // Unqualified but manually moved
        }
        
        if (profileMatchStatus === "qualified") {
          return "success";
        } else if (profileMatchStatus === "unqualified") {
          return "error";
        } else if (profileMatchStatus === "pending") {
          return "neutral";
        }
        
        // If profileMatchResult exists, it means match was done
        // Default to success (qualified) if status is not explicitly set
        if (profileMatchResult) {
          return "success";
        }
        
        // If profileMatchStatus object exists but status field is missing
        if (jobApplication?.profileMatchStatus) {
          return "neutral";
        }
        
        // Resume uploaded but no status information available
        return "neutral";
      }
      
      case "Assessments": {
        if (!isQuestionnaireDone && !isQuestionnairePending) {
          return "neutral"; // Pending
        }
        const questionnaireStatus = jobApplication?.questionnaireStatus;
        const isManuallyMoved = 
          questionnaireStatus === "unqualified" && 
          interviewSessions.length > 0 &&
          (jobApplication?.status === "applied" || jobApplication?.status === "shortlisted" || jobApplication?.status === "interview");
        
        if (isManuallyMoved) {
          return "error"; // Unqualified but manually moved
        }
        
        if (questionnaireStatus === "qualified") {
          return "success";
        } else if (questionnaireStatus === "unqualified") {
          return "error";
        } else if (questionnaireStatus === "pending") {
          return "neutral";
        }
        return "neutral";
      }
      
      case "Online Interview": {
        if (isSlotSelectionDone) {
          return "success"; // Scheduled
        }
        return "neutral"; // Pending
      }
      
      case "Shortlisted": {
        if (isShortlisted) {
          return "success";
        }
        return "neutral";
      }
      
      case "Hired": {
        return "success";
      }
      
      case "Rejected": {
        return "error";
      }
      
      case "Hire/Reject": {
        return "neutral"; // Not yet decided
      }
      
      default:
        return "neutral";
    }
  };

  // Build all possible stages based on job post configuration
  const stepsData: Array<{
    title: string;
    isActive: boolean;
    isCurrent: boolean;
    semanticState: "success" | "error" | "neutral";
  }> = [
    {
      title: "Application",
      isActive: true,
      isCurrent: false,
      semanticState: getStepSemanticState("Application"),
    },
    {
      title: "Profile Match",
      isActive: isResumeUploaded,
      isCurrent: jobApplication?.status === "applied" && !isResumeUploaded,
      semanticState: getStepSemanticState("Profile Match"),
    },
  ];

  // Always show Assessments if job post has interview rounds configured
  if (hasInterviewRounds) {
    stepsData.push({
      title: "Assessments",
      isActive: isQuestionnaireDone,
      isCurrent: isQuestionnairePending,
      semanticState: getStepSemanticState("Assessments"),
    });
  }

  // Show Online Interview only if configured in the job post
  if (Boolean(jobPost?.onlineInterview)) {
    stepsData.push({
      title: "Online Interview",
      isActive: isSlotSelectionDone,
      isCurrent: isSlotSelectionPending,
      semanticState: getStepSemanticState("Online Interview"),
    });
  }

  // Show Shortlisted if it's not an online interview job, or if shortlisted status is reached
  if (!jobPost?.onlineInterview || isShortlisted) {
    stepsData.push({
      title: "Shortlisted",
      isActive: isShortlisted,
      isCurrent: false,
      semanticState: getStepSemanticState("Shortlisted"),
    });
  }

  // Always show final stage (Hire/Reject/Hired/Rejected)
  const finalLabel =
    jobApplication?.status === "hired"
      ? "Hired"
      : jobApplication?.status === "rejected"
      ? "Rejected"
      : "Hire/Reject";
  stepsData.push({
    title: finalLabel,
    isActive:
      jobApplication?.status === "hired" ||
      jobApplication?.status === "rejected",
    isCurrent: finalLabel === "Hire/Reject",
    semanticState: getStepSemanticState(finalLabel),
  });

  // Find the index of the current stage, or the last active stage if no current stage
  const currentStageIndex = stepsData.findIndex(step => step.isCurrent);
  const lastActiveStageIndex = stepsData.map((step, index) => step.isActive ? index : -1).filter(index => index !== -1).pop() ?? -1;
  
  // Map status message to the correct stage based on message content and application state
  const getStatusMessageStageIndex = (): number => {
    if (!currentStatusMessage) return -1;

    const message = currentStatusMessage.toLowerCase();

    // Map "shortlisted" messages to Shortlisted stage
    if (message.includes("shortlisted")) {
      const shortlistedIndex = stepsData.findIndex(step => step.title === "Shortlisted");
      return shortlistedIndex !== -1 ? shortlistedIndex : lastActiveStageIndex;
    }
    
    // Map "online interview" or "scheduled" messages to Online Interview stage
    if (message.includes("online interview") || message.includes("scheduled on")) {
      const onlineInterviewIndex = stepsData.findIndex(step => step.title === "Online Interview");
      return onlineInterviewIndex !== -1 ? onlineInterviewIndex : lastActiveStageIndex;
    }
    
    // Map "Questionnaire Completed" or "Waiting for AI Assessment" messages to Assessments stage
    if (message.includes("questionnaire completed") || 
        message.includes("waiting for ai assessment")) {
      const assessmentsIndex = stepsData.findIndex(step => step.title === "Assessments");
      return assessmentsIndex !== -1 ? assessmentsIndex : lastActiveStageIndex;
    }
    
    // Map "hired" or "rejected" messages to final stage
    if (message.includes("hired") || message.includes("rejected")) {
      const finalStageIndex = stepsData.findIndex(step => 
        step.title === "Hired" || step.title === "Rejected" || step.title === "Hire/Reject"
      );
      return finalStageIndex !== -1 ? finalStageIndex : lastActiveStageIndex;
    }
    
    // Map "Resume Not Uploaded" or "Profile Match Completed" messages to Profile Match stage
    if (message.includes("resume not uploaded") || 
        message.includes("profile match completed")) {
      const profileMatchIndex = stepsData.findIndex(step => step.title === "Profile Match");
      return profileMatchIndex !== -1 ? profileMatchIndex : lastActiveStageIndex;
    }
    
    // Map "Waiting for Interview Slot Selection" to Online Interview stage
    if (message.includes("waiting for interview slot selection")) {
      const onlineInterviewIndex = stepsData.findIndex(step => step.title === "Online Interview");
      return onlineInterviewIndex !== -1 ? onlineInterviewIndex : lastActiveStageIndex;
    }
    
    // Fallback to current stage or last active stage
    return currentStageIndex !== -1 ? currentStageIndex : lastActiveStageIndex;
  };
  
  const statusMessageStageIndex = getStatusMessageStageIndex();

  // Helper function to get subtitle for each step
  const getStepSubtitle = (stepTitle: string, stepIndex: number): string | undefined => {
    switch (stepTitle) {
      case "Applied":
      case "Application": {
        if (jobApplication?.status === "applied" || jobApplication?.createdAt) {
          return "Applied on " + dayjs(jobApplication?.createdAt).format("DD MMM YYYY") + " at " + dayjs(jobApplication?.createdAt).format("hh:mm A");
        }
        return undefined;
      }
      case "Profile Match": {
        // First check if resume was uploaded (profileId exists)
        const hasResumeUploaded = Boolean(jobApplication?.profileId);
        
        if (!hasResumeUploaded) {
          return "Pending";
        }
        
        // Use profileMatchResult status as primary source of truth (same as Profile Match tab)
        // Fall back to jobApplication.profileMatchStatus.status if profileMatchResult is not available
        const statusFromResult = profileMatchResult?.status;
        const profileMatchStatus = statusFromResult || jobApplication?.profileMatchStatus?.status;
        
        // Check if manually moved: unqualified profile match but still moved to AI assessment
        const isManuallyMovedToAI = 
          profileMatchStatus === "unqualified" && 
          interviewSessions.length > 0 &&
          hasInterviewRounds &&
          (jobApplication?.status === "applied" || jobApplication?.status === "shortlisted" || jobApplication?.status === "interview");
        
        if (isManuallyMovedToAI) {
          return "Unqualified - Manually Moved to AI Assessment.";
        }
        
        if (profileMatchStatus === "qualified") {
          return "Qualified";
        } else if (profileMatchStatus === "unqualified") {
          return "Unqualified";
        } else if (profileMatchStatus === "pending") {
          return "Pending";
        }
        
        // If profileMatchResult exists, it means match was done
        // Default to "Qualified" if status is not explicitly set (common case)
        if (profileMatchResult) {
          return "Qualified";
        }
        
        // If profileMatchStatus object exists but status field is missing
        if (jobApplication?.profileMatchStatus) {
          return "Pending";
        }
        
        // Resume uploaded but no status information available
        return "Pending";
      }
      
      case "Assessments": {
        if (!isQuestionnaireDone && !isQuestionnairePending) {
          return "Pending";
        }
        const questionnaireStatus = jobApplication?.questionnaireStatus;
        // Check if manually moved: unqualified status but still has active sessions or moved forward
        const isManuallyMoved = 
          jobPost.isProfileMatchRequired && 
          jobApplication?.profileMatchStatus?.status === "unqualified" &&
          interviewSessions.length > 0 &&
          (jobApplication?.status === "applied" || jobApplication?.status === "shortlisted" || jobApplication?.status === "interview");
        
        if (isManuallyMoved) {
          return "Unqualified - Manually Moved.";
        }
        
        if (questionnaireStatus === "qualified") {
          return "Qualified";
        } else if (questionnaireStatus === "unqualified") {
          return "Unqualified";
        } else if (questionnaireStatus === "pending") {
          return "Pending";
        } else if (questionnaireStatus === "partially_qualified") {
          // Return null to indicate we'll show a button instead
          return undefined;
        }
        return undefined;
      }
      
      case "Online Interview": {
        if (isSlotSelectionDone && jobApplication?.interviewSchedule) {
          const scheduleDate = dayjs(jobApplication.interviewSchedule);
          return `Scheduled on ${scheduleDate.format("DD MMM YYYY")} at ${scheduleDate.format("hh:mm A")}`;
        }
        return undefined;
      }
      
      case "Shortlisted": {
        if (isShortlisted && jobApplication?.updatedAt) {
          const updatedDate = dayjs(jobApplication.updatedAt);
          return `Marked on ${updatedDate.format("DD MMM YYYY")} at ${updatedDate.format("hh:mm A")}`;
        }
        return undefined;
      }
      
      case "Hired": {
        if (jobApplication?.status === "hired") {
          if (jobApplication?.updatedAt) {
            const updatedDate = dayjs(jobApplication.updatedAt);
            return `Marked as Hired on ${updatedDate.format("DD MMM YYYY")} at ${updatedDate.format("hh:mm A")}`;
          }
          return "Marked as Hired";
        }
        return undefined;
      }
      
      case "Rejected": {
        if (jobApplication?.status === "rejected") {
          if (jobApplication?.updatedAt) {
            const updatedDate = dayjs(jobApplication.updatedAt);
            return `Marked as Rejected on ${updatedDate.format("DD MMM YYYY")} at ${updatedDate.format("hh:mm A")}`;
          }
          return "Marked as Rejected";
        }
        return undefined;
      }
      
      case "Hire/Reject": {
        // Show nothing for pending Hire/Reject step
        return undefined;
      }
      
      default:
        return undefined;
    }
  };

  // Convert to Ant Design Steps format
  const stepsItems: StepsProps['items'] = stepsData.map((step, index) => {
    const isCompleted = step.isActive && !step.isCurrent;
    const isCurrent = step.isCurrent;
    const isPending = !step.isActive && !step.isCurrent;
    
    let status: "wait" | "process" | "finish" | "error" = "wait";
    if (isCompleted) {
      status = "finish";
    } else if (isCurrent) {
      status = "process";
    } else if (isPending) {
      status = "wait";
    }

    // Check if this is the Assessments step with partially_qualified status
    const isPartiallyQualifiedAssessment = 
      step.title === "Assessments" && 
      jobApplication?.questionnaireStatus === "partially_qualified";
    
    // Get subtitle for this step
    const subtitle = getStepSubtitle(step.title, index);
    
    // Use subtitle if available, otherwise fall back to currentStatusMessxage for the mapped stage
    // But skip fallback for partially_qualified assessments as we'll show a button instead
    const descriptionText = isPartiallyQualifiedAssessment 
      ? undefined 
      : (subtitle || (index === statusMessageStageIndex && currentStatusMessage ? currentStatusMessage : undefined));
    
    // Check if description contains "Manually Moved" and apply error color styling
    // Check if description contains "Qualified" and apply success color styling
    // Wrap in Tooltip and limit to 2 lines with ellipses
    let description: string | React.ReactNode = descriptionText;
    if (isPartiallyQualifiedAssessment) {
      // Show "Action Required" button for partially qualified assessments
      description = (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            // Use handleTabChange to properly update both state and URL
            setActiveTabKey("assessments");
          }}
          style={{ marginTop: "4px" }}
        >
          Action Required
        </Button>
      );
    } else if (descriptionText) {
      const lowerText = descriptionText.toLowerCase();
      const descriptionContent = lowerText.includes("manually moved") ? (
        <span className="manually-moved-description">{descriptionText}</span>
      ) : lowerText.includes("qualified") ? (
        <span className="qualified-description">{descriptionText}</span>
      ) : (
        descriptionText
      );
      
      // Wrap description in Tooltip to show full text on hover
      description = (
        <Tooltip title={descriptionText} placement="top">
          <span className="step-description-text">{descriptionContent}</span>
        </Tooltip>
      );
    }

    return {
      title: step.title,
      status: status,
      description: description,
      className: `step-semantic-${step.semanticState}`, 
      //icon: <UserOutlined />,
      icon: getStepIcon(step.semanticState) // Add semantic state class
    };
  });

  return (
    <div className="application-stage-steps-container">
      <Steps
        type="navigation"
        size="small"
        items={stepsItems}
        current={currentStageIndex !== -1 ? currentStageIndex : lastActiveStageIndex}
        onChange={() => {}} // Disable navigation by providing empty handler
        className="application-stage-steps"
        
      />
    </div>
  );
};

export default ApplicationStageSteps;
